<template>
  <div class="flex flex-col">
    <!-- Header -->
    <div class="pt-6 px-6">
      <div class="flex flex-col gap-1 pr-8">
        <p class="text-[18px] leading-7 font-semibold text-[#101828] break-words">
          {{ conceptTitle }}
        </p>
        <p class="text-sm leading-5 font-semibold text-[#475467]">
          {{ levelLabel }}<span v-if="levelPercentage !== null">: {{ Math.round(levelPercentage) }}%</span>
        </p>
      </div>
      <div class="h-5"></div>
    </div>

    <!-- Body -->
    <div class="px-6">
      <div
        v-if="conceptsStore.loader.fetchConceptStudents"
        class="flex items-center justify-center py-10"
      >
        <spinner-loader :color="'text-blue-600 dark:text-blue-600'" />
      </div>

      <div v-else class="flex flex-col gap-5">
        <!-- Capability statements -->
        <div v-if="capabilityLines.length > 0" class="flex flex-col gap-3">
          <div
            v-for="(line, index) in capabilityLines"
            :key="index"
            class="flex items-start gap-2"
          >
            <span class="shrink-0 pt-0.5">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="M0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8Z"
                  fill="#7453ED"
                />
                <path
                  d="M11.3333 4.66667L4.66667 11.3333M4.66667 4.66667L11.3333 11.3333"
                  stroke="white"
                  stroke-width="1.33333"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
            <span class="text-sm leading-5 font-medium text-[#344054]">{{ line }}</span>
          </div>
        </div>
        <p v-else class="text-sm text-[#475467] italic">
          {{ strapiError || $t("concepts_view.modal.no_description") }}
        </p>

        <!-- Students at this level -->
        <div v-if="students.length > 0" class="flex flex-col gap-3">
          <p
            v-for="student in students"
            :key="student.userId"
            class="text-sm leading-5 font-medium text-black"
          >
            {{ student.userFullName }}
          </p>
        </div>
        <p v-else class="text-sm text-[#475467] italic">
          {{ $t("concepts_view.modal.no_students") }}
        </p>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex gap-3 pt-8 px-6 pb-6">
      <button
        type="button"
        class="flex-1 rounded-lg border border-[#D0D5DD] bg-white px-4 py-2.5 text-base font-semibold text-[#344054] hover:bg-gray-50"
        @click="$emit('close')"
      >
        {{ $t("concepts_view.modal.cancel") }}
      </button>
      <button
        type="button"
        class="flex-1 rounded-lg border border-[#4F7ED4] bg-[#4F7ED4] px-4 py-2.5 text-base font-semibold text-white hover:bg-[#4571c4]"
        @click="$emit('close')"
      >
        {{ $t("concepts_view.modal.confirm") }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useConceptsStore } from "@/stores/conceptsStore";

defineProps({
  conceptTitle: { type: String, default: "" },
  levelLabel: { type: String, default: "" },
  levelPercentage: { type: Number, default: null },
});

defineEmits(["close"]);

const conceptsStore = useConceptsStore();

const data = computed(() => conceptsStore.getConceptStudents);

const students = computed(() => data.value?.students || []);

const strapiError = computed(() => data.value?.strapiError || null);

// The Strapi capabilities description may contain multiple lines; render each.
const capabilityLines = computed(() => {
  const text = data.value?.concept?.capabilities;
  if (!text || typeof text !== "string" || !text.trim()) return [];
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
});
</script>
